import classNames from "classnames";
import styles from "./AddOrganisation.module.scss";
import { useTranslation } from "react-i18next";
import { useState } from "react";
import { useFormContext } from "react-hook-form";
import { toast } from "react-toastify";
import Modal from "../../common/Modal";
import PrimaryButton from "../../common/PrimaryButton";
import OutlinedButton from "../../common/OutlinedButton";
import { resendClientAdminInvite } from "../../../setup/api/services/user";
import { parseApiErrors } from "../../../setup/util/apiErrors";

export interface IInviteResult {
  userId?: string;
  /** The invitation email went out. */
  sent: boolean;
  /** The person already had an account, so no invitation was needed. */
  existingAccount: boolean;
}

interface ISuccessModalProps {
  handleClose: () => void;
  modalOpen: boolean;
  invite?: IInviteResult;
}

const SuccessModal = ({ handleClose, modalOpen, invite }: ISuccessModalProps) => {
  const { getValues } = useFormContext();
  const { t } = useTranslation();
  const { email } = getValues();
  const [sent, setSent] = useState<boolean | undefined>();
  const [resending, setResending] = useState(false);

  const inviteSent = sent ?? !!invite?.sent;
  const existingAccount = !!invite?.existingAccount;

  let description = t("lbl_invite_not_sent");
  if (existingAccount) {
    description = t("lbl_invite_existing_account");
  } else if (inviteSent) {
    description = t("lbl_invite_sent", { email });
  }

  const onResend = async () => {
    if (!invite?.userId) return;
    setResending(true);
    try {
      await resendClientAdminInvite(invite.userId);
      setSent(true);
      toast.success(t("lbl_invite_resent"));
    } catch (error) {
      toast.error(
        parseApiErrors(error, t("lbl_invite_resend_failed"))[0].message
      );
    } finally {
      setResending(false);
    }
  };

  const onClose = () => {
    setSent(undefined);
    handleClose();
  };

  return (
    <Modal
      visible={modalOpen}
      onCancel={onClose}
      customModalClassName={styles.modalContainer}>
      <div className={classNames(styles.success_container)}>
        <img src="/assets/images/svg/Success_tick.svg" alt="success" />

        <div className={styles.headerContainer}>
          <h1 className={styles.title}>{t("organization_success_title")} </h1>
          <p className={styles.description}>{description}</p>
        </div>

        <div className={styles.dataContainer}>
          <div className={styles.dataContentContainer}>
            <img src="/assets/images/svg/email.svg" alt="email" />
            <div className={styles.dataContent}>
              <p className={styles.dataTitle}>{t("email")}</p>
              <p className={styles.dataDescription}>{email}</p>
            </div>
          </div>
        </div>

        {!existingAccount && invite?.userId && (
          <PrimaryButton
            onClick={onResend}
            disabled={resending}
            style={{
              width: "100%",
              height: "48px",
              borderRadius: "8px",
              fontSize: "1rem",
              fontWeight: 600,
              marginTop: "40px",
              background: "#0D44AD",
            }}>
            {inviteSent ? t("lbl_resend_invite") : t("lbl_send_invite")}
          </PrimaryButton>
        )}
        <OutlinedButton
          onClick={onClose}
          style={{
            width: "100%",
            color: "#0C4BC4",
            background: "#0C4BC41A",
            border: "none",
            fontSize: "1rem",
            fontWeight: 400,
            height: "48px",
            marginTop: "15px",
          }}>
          {t("close")}
        </OutlinedButton>
      </div>
    </Modal>
  );
};

export default SuccessModal;
